import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';

const BasketContext = createContext(null);

// Each customer account keeps its own basket in this browser. A buyer who
// orders for two properties signs out of one and into the other; their first
// property's basket must never be placed on the second's account. Signed out,
// the basket is empty; signing back in brings that account's basket back.
const keyFor = (owner, name) => `ss_${name}:${owner}`;
function loadFor(owner) {
  const empty = { items: [], editOrder: null, unviewed: false };
  if (!owner) return empty;
  try {
    let items = localStorage.getItem(keyFor(owner, 'basket'));
    // Baskets saved before they were kept per account: the first account to
    // sign in on this browser adopts it, once.
    if (items === null && localStorage.getItem('ss_basket') !== null) {
      items = localStorage.getItem('ss_basket');
      const edit = localStorage.getItem('ss_edit_order');
      if (edit) localStorage.setItem(keyFor(owner, 'edit_order'), edit);
      ['ss_basket', 'ss_edit_order', 'ss_basket_unviewed'].forEach((k) => localStorage.removeItem(k));
    }
    const edit = localStorage.getItem(keyFor(owner, 'edit_order'));
    return {
      items: items ? JSON.parse(items) : [],
      editOrder: edit ? JSON.parse(edit) : null,
      unviewed: localStorage.getItem(keyFor(owner, 'basket_unviewed')) === '1',
    };
  } catch { return empty; }
}

// Drops one account's saved basket from this browser.
export function forgetSavedBasket(owner) {
  if (owner == null) return;
  try {
    ['basket', 'edit_order', 'basket_unviewed'].forEach((name) => localStorage.removeItem(keyFor(String(owner), name)));
  } catch { /* storage blocked */ }
}

export function BasketProvider({ children }) {
  const { user } = useAuth() || {};
  const owner = user && user.customerId != null ? String(user.customerId) : null;
  const initial = useRef(null);
  if (initial.current === null) initial.current = loadFor(owner);

  const [items, setItems] = useState(initial.current.items);

  // Drives the mobile basket bar visibility: true when the user has added
  // items they haven't yet seen on the basket page. Flips back to false
  // when the basket page renders (markBasketViewed). Persisted so the
  // bar's state survives reloads.
  const [hasUnviewedItems, setHasUnviewedItems] = useState(initial.current.unviewed);

  // When set, the basket is editing an existing order rather than building a new one.
  const [editOrder, setEditOrder] = useState(initial.current.editOrder);

  // Whose basket is in state. Saving waits until it matches the signed-in
  // account, so switching accounts can't write one basket under the other.
  const [stateOwner, setStateOwner] = useState(owner);
  useEffect(() => {
    if (owner === stateOwner) return;
    const next = loadFor(owner);
    setItems(next.items);
    setEditOrder(next.editOrder);
    setHasUnviewedItems(next.unviewed);
    setStateOwner(owner);
  }, [owner, stateOwner]);

  useEffect(() => {
    if (!owner || owner !== stateOwner) return;
    try { localStorage.setItem(keyFor(owner, 'basket'), JSON.stringify(items)); } catch { /* storage full or blocked */ }
  }, [items, owner, stateOwner]);

  useEffect(() => {
    if (!owner || owner !== stateOwner) return;
    try {
      if (editOrder) localStorage.setItem(keyFor(owner, 'edit_order'), JSON.stringify(editOrder));
      else localStorage.removeItem(keyFor(owner, 'edit_order'));
    } catch { /* storage blocked */ }
  }, [editOrder, owner, stateOwner]);

  useEffect(() => {
    if (!owner || owner !== stateOwner) return;
    try { localStorage.setItem(keyFor(owner, 'basket_unviewed'), hasUnviewedItems ? '1' : '0'); } catch { /* storage blocked */ }
  }, [hasUnviewedItems, owner, stateOwner]);

  function addItems(newItems) {
    setItems(prev => {
      const updated = [...prev];
      for (const item of newItems) {
        const idx = updated.findIndex(i => i.itemId === item.itemId);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + item.quantity };
        } else {
          updated.push({
            itemId: item.itemId,
            sosId: item.sosId,
            name: item.name,
            // Catalog "description" is the long-form detail (pack size,
            // weight, etc.) the warehouse picks/packs by — preserve it
            // so it makes it onto the SOS line description at checkout.
            description: item.description || '',
            sku: item.sku,
            quantity: item.quantity,
            price: item.price,
            // Sale fields are display-only — the server reprices every line
            // authoritatively at checkout, so a stale basket can't mis-charge.
            onSale: !!item.onSale,
            originalPrice: item.originalPrice ?? item.price,
            saleLabel: item.saleLabel || null,
          });
        }
      }
      return updated;
    });
    setHasUnviewedItems(true);
  }

  function updateQuantity(itemId, quantity) {
    if (quantity === 0) {
      removeItem(itemId);
      return;
    }
    setItems(prev => prev.map(i => i.itemId === itemId ? { ...i, quantity } : i));
  }

  function removeItem(itemId) {
    setItems(prev => prev.filter(i => i.itemId !== itemId));
  }

  function clearBasket() {
    setItems([]);
    setHasUnviewedItems(false);
  }

  function markBasketViewed() {
    setHasUnviewedItems(false);
  }

  // Load an existing order's lines into the basket for editing, replacing current contents.
  function startEditOrder(order, lines) {
    setItems(lines);
    setEditOrder({ id: order.id, number: order.number || `Order #${order.id}` });
    setHasUnviewedItems(true);
  }

  function cancelEditOrder() {
    setEditOrder(null);
    setItems([]);
  }

  const itemCount = items.length;
  const basketTotal = items.reduce((sum, i) => sum + (i.price || 0) * i.quantity, 0);

  return (
    <BasketContext.Provider value={{ items, addItems, updateQuantity, removeItem, clearBasket, itemCount, basketTotal, hasUnviewedItems, markBasketViewed, editOrder, startEditOrder, cancelEditOrder }}>
      {children}
    </BasketContext.Provider>
  );
}

export const useBasket = () => useContext(BasketContext);
